/** Local actor queries use numeric bins and an occupied range. Sparse hazards
 * must not allocate/scan a wide empty grid for every resident on every tick. */
export class Spatial<T extends { p: { x: number; z: number } }> {
  private columns = new Map<number, Map<number, T[]>>();
  private membership = new Map<T, { x: number; z: number; bin: T[]; generation: number }>();
  private generation = 0;
  private minX = Infinity;
  private maxX = -Infinity;
  private minZ = Infinity;
  private maxZ = -Infinity;
  constructor(readonly size = 16) {}
  rebuild(items: T[]): void {
    // Stable actors normally remain in the same bins. Reconcile membership
    // instead of allocating the entire spatial grid again after each blast.
    this.generation++;
    for (const item of items) this.update(item);
    for (const [item, prior] of this.membership)
      if (prior.generation !== this.generation) {
        prior.bin.splice(prior.bin.indexOf(item), 1);
        this.membership.delete(item);
      }
  }
  /** Moving actors change bins immediately; unchanged occupants cost one lookup. */
  update(item: T): void {
    const x = Math.floor(item.p.x / this.size),
      z = Math.floor(item.p.z / this.size);
    const prior = this.membership.get(item);
    if (prior) prior.generation = this.generation;
    if (prior?.x === x && prior.z === z) return;
    if (prior) prior.bin.splice(prior.bin.indexOf(item), 1);
    this.minX = Math.min(this.minX, x);
    this.maxX = Math.max(this.maxX, x);
    this.minZ = Math.min(this.minZ, z);
    this.maxZ = Math.max(this.maxZ, z);
    let column = this.columns.get(x);
    if (!column) this.columns.set(x, (column = new Map()));
    let bin = column.get(z);
    if (!bin) column.set(z, (bin = []));
    bin.push(item);
    if (prior) {
      prior.x = x;
      prior.z = z;
      prior.bin = bin;
    } else this.membership.set(item, { x, z, bin, generation: this.generation });
  }
  query(point: { x: number; z: number }, radius: number, visit: (item: T) => void): void {
    const x0 = Math.max(this.minX, Math.floor((point.x - radius) / this.size)),
      x1 = Math.min(this.maxX, Math.floor((point.x + radius) / this.size)),
      z0 = Math.max(this.minZ, Math.floor((point.z - radius) / this.size)),
      z1 = Math.min(this.maxZ, Math.floor((point.z + radius) / this.size)),
      squared = radius * radius;
    for (let x = x0; x <= x1; x++) {
      const column = this.columns.get(x);
      if (!column) continue;
      for (let z = z0; z <= z1; z++) {
        const bin = column.get(z);
        if (!bin) continue;
        for (const item of bin) {
          const dx = item.p.x - point.x,
            dz = item.p.z - point.z;
          if (dx * dx + dz * dz <= squared) visit(item);
        }
      }
    }
  }
  clear(): void {
    this.columns.clear();
    this.membership.clear();
    this.minX = this.minZ = Infinity;
    this.maxX = this.maxZ = -Infinity;
  }
}
